import { documentToReactComponents, type Options, type RenderNode } from "@contentful/rich-text-react-renderer";
import { BLOCKS, type Block, type Document, type Inline, type Text } from "@contentful/rich-text-types";
import { Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from "@tine/designsystem-ui-react";
import React, { type ReactNode } from "react";

import { extractTextFromReactNode } from "../../common/utils";

export type TextTags = "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "ul" | "ol" | "table" | "li" | "hr" | "blockquote";

export const RichTextColumn = ({ children }: { children: ReactNode }) => {
   return <div className="w-full max-w-2xl mx-auto text-md md:text-lg">{children}</div>;
};

export const RichTextListItem = ({ node, customRenderNodes }: { node: Block | Inline; customRenderNodes: RenderNode }) => {
   const listItemDocument: Document = {
      nodeType: BLOCKS.DOCUMENT,
      content: [{ nodeType: BLOCKS.EMBEDDED_RESOURCE, content: node.content, data: {} }],
      data: {}
   };
   const customRenderOptions: Options = {
      renderNode: {
         ...customRenderNodes
      }
   };
   return <li className="list-outside">{documentToReactComponents(listItemDocument, customRenderOptions)}</li>;
};

export const RichTextTable = ({ table, customRenderNodes }: { table: Block | Inline; customRenderNodes: RenderNode }) => {
   const toBlockOrInline = (item: Text | Block | Inline): Block | Inline | undefined => {
      if ("content" in item) {
         return item as Block | Inline;
      }
      return undefined;
   };

   const hasHeader = table.content.map((rows) =>
      toBlockOrInline(rows)?.content.find((cell) => cell.nodeType === "table-header-cell")
   );

   const headerRow = hasHeader ? toBlockOrInline(table.content[0])?.content : undefined;
   const tableRows = hasHeader ? table.content.slice(1) : table.content;

   const getHeaderName = (index: number) => {
      const headerCell = headerRow && toBlockOrInline(headerRow[index]);
      const headerContent = headerCell && getCellContent(headerCell);

      if (React.isValidElement(headerContent)) {
         return extractTextFromReactNode(headerContent);
      }

      return "";
   };

   const getCellContent = (cell: Block | Inline) => {
      const tableCellDocument: Document = {
         nodeType: BLOCKS.DOCUMENT,
         content: [
            {
               nodeType: BLOCKS.EMBEDDED_RESOURCE,
               content: cell.content,
               data: {}
            }
         ],
         data: {}
      };
      const customRenderOptions: Options = {
         renderNode: {
            ...customRenderNodes
         }
      };
      return <>{documentToReactComponents(tableCellDocument, customRenderOptions)}</>;
   };

   return (
      <Table fullWidth className="my-8">
         {headerRow && (
            <TableHeader>
               <TableRow>
                  {headerRow.map((_, cellIndex) => (
                     <TableHeaderCell id={cellIndex.toString()} key={`${cellIndex + 1}`}>
                        {getHeaderName(cellIndex)}
                     </TableHeaderCell>
                  ))}
               </TableRow>
            </TableHeader>
         )}
         <TableBody>
            {/* biome-ignore lint/suspicious/noExplicitAny:  contentful types are too complex to type here */}
            {tableRows.map((row: any, rowIndex: number) => (
               <TableRow key={`${rowIndex + 1}`}>
                  {row.content.map((cell: Block | Inline, cellIndex: number) => (
                     <TableCell headers={getHeaderName(cellIndex)} key={`${cellIndex + 1}`}>
                        {getCellContent(cell)}
                     </TableCell>
                  ))}
               </TableRow>
            ))}
         </TableBody>
      </Table>
   );
};
